import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, Clock3, Eye, Gavel, IndianRupee, Plus, RefreshCw, Trophy, TrendingUp, Users, X, CheckCircle, AlertCircle, Timer, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";
import { filterRank } from "@/lib/adminSearch";

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;
const fmt = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const remaining = (date) => {
  const ms = Math.max(0, new Date(date).getTime() - Date.now());
  if (ms === 0) return "Ended";
  const h = Math.floor(ms / 3600000), m = Math.floor(ms / 60000) % 60, s = Math.floor(ms / 1000) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const STATUS_BADGE = {
  live: { label: "LIVE", color: "#22c55e", bg: "#22c55e18" },
  upcoming: { label: "UPCOMING", color: "#f59e0b", bg: "#f59e0b18" },
  closed: { label: "CLOSED", color: "#6b7f9e", bg: "#6b7f9e18" },
};

function CreateAuctionModal({ products, onClose, onCreated }) {
  const [form, setForm] = useState({ product_id: "", starting_price: "", bid_increment: 500, duration: 24, ends_at: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const product = products.find(p => p.id === form.product_id);
      const start = new Date();
      const end = form.ends_at ? new Date(form.ends_at) : new Date(start.getTime() + Number(form.duration) * 3600000);
      await api.post("/api/admin/auctions", {
        product_id: form.product_id,
        starting_price: Number(form.starting_price || product?.price || 0),
        bid_increment: Number(form.bid_increment),
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
      });
      toast.success("Auction live ho gaya!");
      onCreated();
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const selectedProduct = products.find(p => p.id === form.product_id);

  return (
    <div className="auction-modal-backdrop" data-testid="auction-create-modal">
      <form className="auction-create-modal" onSubmit={submit} style={{ maxWidth: 520, width: "90%" }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <span style={{ fontSize: 10, color: "#00d4ff", fontWeight: 800, letterSpacing: "0.1em" }}>NEW AUCTION</span>
            <h2 style={{ margin: "4px 0 0", fontSize: 20 }}>Create Live Auction</h2>
          </div>
          <button type="button" onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: "#1a2840", border: "1px solid #3a5070", color: "#8899b0", cursor: "pointer" }} data-testid="auction-create-close"><X size={16} /></button>
        </header>

        {/* Product select */}
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "#8899b0" }}>Product select karo *</span>
          <select required value={form.product_id} onChange={e => set("product_id", e.target.value)} data-testid="auction-product-select"
            style={{ display: "block", width: "100%", marginTop: 4, padding: "10px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }}>
            <option value="">— Product select karo —</option>
            {products.filter(p => p.active && p.stock > 0).map(p => (
              <option key={p.id} value={p.id}>{p.name} · ₹{Number(p.price).toLocaleString("en-IN")}</option>
            ))}
          </select>
        </label>

        {selectedProduct && (
          <div style={{ display: "flex", gap: 10, padding: 10, borderRadius: 8, background: "#0d1f35", border: "1px solid #1e3050", marginBottom: 12 }}>
            <img src={selectedProduct.images?.[0] || selectedProduct.image} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: "contain", background: "#060c1a" }} />
            <div>
              <b style={{ fontSize: 13, color: "#e0e8f0" }}>{selectedProduct.name}</b>
              <p style={{ fontSize: 11, color: "#6b7f9e", margin: "2px 0 0" }}>MRP: {money(selectedProduct.original_price || selectedProduct.price)}</p>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <label>
            <span style={{ fontSize: 12, color: "#8899b0" }}>Starting Bid (₹) *</span>
            <input type="number" min="1" required value={form.starting_price} onChange={e => set("starting_price", e.target.value)} placeholder={selectedProduct ? selectedProduct.price : "e.g. 25000"} data-testid="auction-starting-bid-input"
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }} />
          </label>
          <label>
            <span style={{ fontSize: 12, color: "#8899b0" }}>Bid Increment (₹)</span>
            <input type="number" min="1" value={form.bid_increment} onChange={e => set("bid_increment", Number(e.target.value))} data-testid="auction-bid-increment-input"
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }} />
          </label>
        </div>

        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "#8899b0" }}>Duration</span>
          <select value={form.duration} onChange={e => set("duration", e.target.value)} data-testid="auction-duration-select"
            style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13 }}>
            <option value="1">1 ghanta</option>
            <option value="3">3 ghante</option>
            <option value="6">6 ghante</option>
            <option value="12">12 ghante</option>
            <option value="24">24 ghante (1 din)</option>
            <option value="48">48 ghante (2 din)</option>
            <option value="72">72 ghante (3 din)</option>
          </select>
        </label>

        <button className="auction-create-button" type="submit" disabled={busy} data-testid="auction-create-submit"
          style={{ width: "100%", padding: "12px 0", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 14, fontWeight: 800 }}>
          {busy ? <RefreshCw size={15} className="spin-slow" /> : <Gavel size={15} />}
          {busy ? "Creating…" : "Launch Auction Live"}
        </button>
      </form>
    </div>
  );
}

function AuctionCard({ row, bids, onClose, onExtend, clock }) {
  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.closed;
  const rowBids = bids.filter(b => b.auction_id === row.id);

  return (
    <article className="auction-admin-card" data-testid={`auction-card-${row.id}`} style={{ position: "relative", overflow: "hidden" }}>
      <div className="auction-card-media">
        <img src={row.product?.images?.[0] || row.product?.image} alt={row.product?.name} />
        <span style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}44`, padding: "3px 7px", borderRadius: 4, fontSize: 9, fontWeight: 900 }}>{badge.label}</span>
        <small><Eye size={11} /> {row.bid_count}</small>
      </div>
      <div style={{ padding: "10px 12px" }}>
        <h3 style={{ fontSize: 13, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.product?.name}</h3>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <small style={{ color: "#6b7f9e", fontSize: 10 }}>Current bid</small>
            <b style={{ display: "block", color: "#f59e0b", fontSize: 16, fontWeight: 800 }}>{money(row.current_bid)}</b>
          </div>
          {row.status === "live" && (
            <div style={{ textAlign: "right" }}>
              <small style={{ color: "#6b7f9e", fontSize: 10 }}>Ends in</small>
              <b style={{ display: "block", color: "#22c55e", fontSize: 12, fontFamily: "monospace" }}>{remaining(row.ends_at)}</b>
            </div>
          )}
        </div>
        <div style={{ fontSize: 10, color: "#4a6080", marginBottom: 8 }}>
          Start: {money(row.starting_price)} · Incr: {money(row.bid_increment)}
        </div>
        {row.winner_name && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderRadius: 6, background: "#f59e0b11", border: "1px solid #f59e0b33", marginBottom: 6 }}>
            <Trophy size={12} color="#f59e0b" />
            <span style={{ fontSize: 11, color: "#f59e0b" }}>Winner: {row.winner_name} · {money(row.current_bid)}</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          {row.status === "live" && (
            <>
              <button onClick={() => onClose(row)} data-testid={`close-auction-${row.id}`}
                style={{ flex: 1, padding: "6px 0", borderRadius: 6, background: "#ef444418", border: "1px solid #ef444433", color: "#ef4444", cursor: "pointer", fontSize: 10, fontWeight: 700 }}>
                Close Now
              </button>
              <button onClick={() => onExtend(row)} data-testid={`extend-auction-${row.id}`}
                style={{ flex: 1, padding: "6px 0", borderRadius: 6, background: "#00d4ff11", border: "1px solid #00d4ff33", color: "#00d4ff", cursor: "pointer", fontSize: 10, fontWeight: 700 }}>
                +1hr Extend
              </button>
            </>
          )}
          {row.status === "closed" && !row.winner_name && row.bid_count > 0 && (
            <button style={{ width: "100%", padding: "6px 0", borderRadius: 6, background: "#f59e0b11", border: "1px solid #f59e0b33", color: "#f59e0b", cursor: "pointer", fontSize: 10, fontWeight: 700 }}
              data-testid={`declare-winner-${row.id}`}>
              <Trophy size={11} /> Declare Winner
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function AdminAuctionDashboard({ query = "" }) {
  const [auctions, setAuctions] = useState([]);
  const [products, setProducts] = useState([]);
  const [bids, setBids] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("live");
  const [clock, setClock] = useState(Date.now());
  const order = useRef([]);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.get("/api/admin/auctions"), api.get("/api/admin/products?page_size=200")]);
      order.current = [
        ...order.current.filter(id => a.data.some(r => r.id === id)),
        ...a.data.map(r => r.id).filter(id => !order.current.includes(id)),
      ];
      setAuctions([...a.data].sort((x, y) => order.current.indexOf(x.id) - order.current.indexOf(y.id)));
      setProducts(p.data.items);
      const rows = await Promise.all(
        a.data.filter(r => r.status === "live").slice(0, 12).map(r =>
          api.get(`/api/auctions/${r.id}/bids`).then(({ data }) => data.map(b => ({ ...b, auction_id: r.id }))).catch(() => [])
        )
      );
      setBids(rows.flat().sort((x, y) => y.created_at?.localeCompare(x.created_at)).slice(0, 30));
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setClock(Date.now()), 1000); return () => clearInterval(t); }, []);

  const live = auctions.filter(r => r.status === "live");
  const upcoming = auctions.filter(r => r.status === "upcoming");
  const closed = auctions.filter(r => r.status === "closed");

  const stats = useMemo(() => ({
    live: live.length,
    bidders: new Set(bids.map(r => r.user_id)).size,
    bids: live.reduce((n, r) => n + r.bid_count, 0),
    revenue: closed.reduce((n, r) => n + r.current_bid, 0),
  }), [auctions, bids, live, closed]);

  const closeAuction = async (row) => {
    if (!window.confirm(`"${row.product?.name}" auction band karein?`)) return;
    try { await api.post(`/api/admin/auctions/${row.id}/close`); toast.success("Auction closed!"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const extendAuction = async (row) => {
    try {
      const newEnd = new Date(new Date(row.ends_at).getTime() + 3600000);
      await api.patch(`/api/admin/auctions/${row.id}`, { ends_at: newEnd.toISOString() }).catch(() =>
        api.post(`/api/admin/auctions/${row.id}/extend`, { hours: 1 })
      );
      toast.success("1 ghanta extend ho gaya!");
      load();
    } catch (e) { toast.error("Extend API unavailable — auction close karke nayi create karein"); }
  };

  const tabAuctions = tab === "live" ? live : tab === "upcoming" ? upcoming : closed;
  const visible = filterRank(tabAuctions, query, [r => r.product?.name]);

  return (
    <main className="auction-admin" data-testid="auction-dashboard">
      {open && <CreateAuctionModal products={products} onClose={() => setOpen(false)} onCreated={load} />}

      {/* Header */}
      <header className="auction-admin-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <span className="eyebrow">DM MOBILE · LIVE COMMERCE</span>
          <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}><Gavel size={24} /> Auction Dashboard</h1>
          <p style={{ color: "#6b7f9e", fontSize: 13 }}>Create, manage and track every live auction in real-time.</p>
        </div>
        <button className="auction-create-button" onClick={() => setOpen(true)} data-testid="auction-create-open"
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", borderRadius: 10, fontWeight: 800 }}>
          <Plus size={16} /> New Auction
        </button>
      </header>

      {/* Stats */}
      <section className="auction-stat-grid" style={{ gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[[Gavel, "Live Auctions", stats.live, "green"], [Users, "Unique Bidders", stats.bidders, "violet"], [BarChart3, "Total Bids", stats.bids, "blue"], [IndianRupee, "Auction Revenue", money(stats.revenue), "amber"]].map(([Icon, title, value, tone]) => (
          <article className={`auction-stat ${tone}`} key={title} data-testid={`auction-stat-${title.toLowerCase().replaceAll(" ", "-")}`}>
            <span><Icon size={20} /></span>
            <div><small>{title}</small><b>{value}</b><em>Live data</em></div>
          </article>
        ))}
      </section>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, background: "#0a1325", borderRadius: 8, padding: 3, width: "fit-content" }}>
        {[["live", `Live (${live.length})`], ["upcoming", `Upcoming (${upcoming.length})`], ["closed", `Closed (${closed.length})`]].map(([v, t]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ padding: "7px 14px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", background: tab === v ? "#5030a5" : "transparent", color: tab === v ? "#fff" : "#9eb0cd" }}
            data-testid={`auction-admin-tab-${v}`}>{t}</button>
        ))}
      </div>

      {/* Auctions grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px,1fr))", gap: 12, marginBottom: 24 }}>
        {visible.length === 0 ? (
          <div className="auction-empty" data-testid="auction-empty-state" style={{ gridColumn: "1/-1" }}>
            <Gavel size={30} />
            <h3>{tab === "live" ? "Koi live auction nahi hai" : tab === "upcoming" ? "Koi upcoming auction nahi" : "Koi closed auction nahi"}</h3>
            {tab === "live" && <button className="auction-create-button" onClick={() => setOpen(true)}>Pehla auction create karo</button>}
          </div>
        ) : visible.map(row => (
          <AuctionCard key={row.id} row={row} bids={bids} clock={clock} onClose={closeAuction} onExtend={extendAuction} />
        ))}
      </div>

      {/* Bottom row: Recent bids + Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Recent bids */}
        <section className="auction-panel" data-testid="auction-recent-bids-panel" style={{ padding: 16 }}>
          <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h2 style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={15} /> Recent Bids</h2>
            <span style={{ fontSize: 11, color: "#6b7f9e" }}>{bids.length} live bids</span>
          </header>
          {bids.length === 0 ? (
            <p style={{ color: "#6b7f9e", fontSize: 12, textAlign: "center", padding: "20px 0" }}>Abhi koi bids nahi hain</p>
          ) : bids.slice(0, 8).map(row => (
            <div key={row.id} className="recent-bid" data-testid={`auction-recent-bid-${row.id}`}
              style={{ display: "grid", gridTemplateColumns: "30px 1fr auto", gap: 8, alignItems: "center", padding: "7px 0", borderBottom: "1px solid #1a2840" }}>
              <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#362070", color: "#c0a0ff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {row.bidder_name?.[0]?.toUpperCase()}
              </span>
              <p style={{ margin: 0 }}>
                <b style={{ fontSize: 12, display: "block" }}>{row.bidder_name}</b>
                <small style={{ fontSize: 10, color: "#6b7f9e" }}>{fmt(row.created_at)}</small>
              </p>
              <em style={{ color: "#f8d46a", fontStyle: "normal", fontSize: 13, fontWeight: 800 }}>{money(row.amount)}</em>
            </div>
          ))}
        </section>

        {/* Summary */}
        <section className="auction-panel auction-summary" data-testid="auction-summary-panel" style={{ padding: 16 }}>
          <header style={{ marginBottom: 12 }}>
            <h2 style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><BarChart3 size={15} /> Auction Summary</h2>
          </header>
          {[["Total Auctions", auctions.length], ["Live Right Now", live.length], ["Upcoming Scheduled", upcoming.length], ["Completed", closed.length], ["Unique Bidders", stats.bidders], ["Total Bids Placed", stats.bids], ["Auction Revenue", money(stats.revenue)]].map(([label, val]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #1a2840", fontSize: 13 }}>
              <span style={{ color: "#8899b0" }}>{label}</span>
              <b style={{ color: "#e0e8f0" }}>{val}</b>
            </div>
          ))}
          <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 8, background: "linear-gradient(120deg, #1a1060, #0a2848)", border: "1px solid #3a2070" }}>
            <b style={{ fontSize: 16, color: "#f8d46a", display: "block" }}>{money(stats.revenue)}</b>
            <small style={{ color: "#9caed0", fontSize: 11 }}>Total closed auction value</small>
          </div>
        </section>
      </div>
    </main>
  );
}
